import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { stripeApi } from '../api/stripe';
import { useAuth } from '../hooks/useAuth';
import { isAdmin, isSuperAdmin } from '../utils/roles';
import { getProperties } from '../api/properties';
import { getCustomers } from '../api/customers';
import { getUnits } from '../api/units';
import { getDevices } from '../api/devices';
import { apiList } from '../api/apiClient';
import { STRIPE_PROXY_URL, calcDeviceCost, countPremiumDevices, countStandardDevices } from '../config/billing';
import { RatesTable } from '../components/billing/RatesTable';
import { BillableItemsManager } from '../components/billing/BillableItemsManager';
import { ProductsManager } from '../components/billing/ProductsManager';
import { ServiceCostsTab } from '../components/billing/ServiceCostsTab';
import { SubscriptionActions } from '../components/billing/SubscriptionActions';
import { SubscriptionDetailModal } from '../components/billing/SubscriptionDetailModal';
import { CreateSubscriptionModal } from '../components/billing/CreateSubscriptionModal';
import { CreateInvoiceModal } from '../components/billing/CreateInvoiceModal';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';

type BillingTab = 'overview' | 'standard' | 'online' | 'dealers' | 'customers' | 'properties' | 'subscriptions' | 'invoices' | 'products' | 'services';

const statusColor = (s: string) => {
  switch (s) {
    case 'active': case 'paid': return 'green';
    case 'trialing': return 'blue';
    case 'past_due': case 'open': return 'yellow';
    case 'canceled': case 'void': case 'uncollectible': return 'red';
    default: return 'gray';
  }
};
const fmtDate = (ts: number) => ts ? new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtAmount = (cents: number) => cents != null ? `$${(cents / 100).toFixed(2)}` : '—';

/* ═══════════════════════════════════════════════════════════════
   ADMIN BILLING CENTER
   ═══════════════════════════════════════════════════════════════ */

const AdminBilling: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || 'admin';
  const [tab, setTab] = useState<BillingTab>('overview');
  const [loading, setLoading] = useState(true);

  // Data
  const [properties, setProperties] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [dealers, setDealers] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [devices, setDevicesState] = useState<any[]>([]);
  const [allSubs, setAllSubs] = useState<any[]>([]);
  const [allInvoices, setAllInvoices] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);

  // Modals
  const [createSubOpen, setCreateSubOpen] = useState(false);
  const [createInvOpen, setCreateInvOpen] = useState(false);
  const [sendEmailInvId, setSendEmailInvId] = useState<string | null>(null);
  const [sendEmailAddr, setSendEmailAddr] = useState('');
  const [expandedSubId, setExpandedSubId] = useState<string | null>(null);
  const [detailSub, setDetailSub] = useState<any>(null);
  const [expandedInvId, setExpandedInvId] = useState<string | null>(null);

  // Dealer/Customer/Property expansion for rate editing
  const [expandedRateId, setExpandedRateId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [props, custs, dlrs, uts, devs] = await Promise.all([
          getProperties().catch(() => []),
          getCustomers().catch(() => []),
          apiList('integrators').catch(() => []),
          getUnits().catch(() => []),
          getDevices().catch(() => []),
        ]);
        setProperties(props);
        setCustomers(custs);
        setDealers(dlrs);
        setUnits(uts);
        setDevicesState(devs);
      } catch (e) {
        console.error('Billing load error:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Fetch Stripe data when needed
  useEffect(() => {
    if ((tab === 'subscriptions' || tab === 'invoices' || tab === 'overview') && allSubs.length === 0 && !billingLoading) {
      fetchStripeBilling();
    }
  }, [tab]);

  const fetchStripeBilling = async () => {
    setBillingLoading(true);
    try {
      const stripeCusts = customers.filter((c: any) => c.stripe_customer_id);
      const results = await Promise.all(stripeCusts.map(async (c: any) => {
        const [subs, invs] = await Promise.all([
          fetch(`${STRIPE_PROXY_URL}/subscriptions?customer=${c.stripe_customer_id}`).then(r => r.json()).catch(() => []),
          fetch(`${STRIPE_PROXY_URL}/invoices?customer=${c.stripe_customer_id}`).then(r => r.json()).catch(() => []),
        ]);
        const name = c.company || c.company_name || c.name;
        return {
          subs: (Array.isArray(subs) ? subs : subs?.data || []).map((s: any) => ({ ...s, _customerName: name, _customerId: c.id })),
          invs: (Array.isArray(invs) ? invs : invs?.data || []).map((i: any) => ({ ...i, _customerName: name, _customerId: c.id })),
        };
      }));
      setAllSubs(results.flatMap(r => r.subs));
      setAllInvoices(results.flatMap(r => r.invs));
    } catch (e) {
      console.error('Failed to fetch Stripe data:', e);
    } finally {
      setBillingLoading(false);
    }
  };

  // Calculations
  const phones = properties.filter((p: any) => p.telnyx_number).length;
  const totalMonthly = (units.length * 1.5) + calcDeviceCost(devices) + (phones * 3);
  const stripeMonthly = allSubs.filter(s => s.status === 'active' || s.status === 'trialing').reduce((sum, s) => {
    return sum + (s.items?.data || []).reduce((is: number, item: any) => is + ((item.price?.unit_amount || 0) * (item.quantity || 1)), 0);
  }, 0);

  const tabs: { key: BillingTab; label: string; superOnly?: boolean }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'standard', label: 'Standard Rates', superOnly: true },
    { key: 'dealers', label: 'Dealers', superOnly: true },
    { key: 'customers', label: 'Customers', superOnly: true },
    { key: 'properties', label: 'Properties', superOnly: true },
    { key: 'subscriptions', label: 'Subscriptions' },
    { key: 'invoices', label: 'Invoices' },
    { key: 'products', label: 'Products & Prices', superOnly: true },
    { key: 'services', label: 'Services & Costs', superOnly: true },
  ];

  const visibleTabs = tabs.filter(t => !t.superOnly || isSuperAdmin(role));

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /><span className="ml-3 text-gray-500">Loading billing...</span></div>;

  return (
    <div className="shadow-page-frame legacy-dashboard-migrated">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing Center</h1>
          <p className="text-gray-500 mt-1">Manage rates, subscriptions, invoices, and services</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateSubOpen(true)}>+ Subscription</Button>
          <Button variant="secondary" onClick={() => setCreateInvOpen(true)}>+ Invoice</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto border-b border-gray-200">
        {visibleTabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
              tab === t.key ? 'text-brand-600 border-brand-600' : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {/* ═══ OVERVIEW TAB ═══ */}
      {tab === 'overview' && (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card><div className="py-4 text-center"><p className="text-xs text-gray-500">Calculated Revenue</p><p className="text-3xl font-bold text-gray-900">${totalMonthly.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p><p className="text-xs text-gray-400 mt-1">{properties.length} properties</p></div></Card>
            <Card><div className="py-4 text-center"><p className="text-xs text-gray-500">Stripe Revenue</p><p className="text-3xl font-bold text-green-600">{fmtAmount(stripeMonthly)}</p><p className="text-xs text-gray-400 mt-1">{allSubs.filter(s => s.status === 'active').length} active subs</p></div></Card>
            <Card><div className="py-4 text-center"><p className="text-xs text-gray-500">Total Units</p><p className="text-3xl font-bold text-gray-900">{units.length}</p></div></Card>
            <Card><div className="py-4 text-center"><p className="text-xs text-gray-500">Total Devices</p><p className="text-3xl font-bold text-gray-900">{devices.length}</p></div></Card>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card><div className="py-4 text-center"><p className="text-xs text-gray-500">Dealers</p><p className="text-3xl font-bold text-gray-900">{dealers.length}</p></div></Card>
            <Card><div className="py-4 text-center"><p className="text-xs text-gray-500">Customers</p><p className="text-3xl font-bold text-gray-900">{customers.length}</p></div></Card>
            <Card><div className="py-4 text-center"><p className="text-xs text-gray-500">Phone Numbers</p><p className="text-3xl font-bold text-gray-900">{phones}</p></div></Card>
            <Card><div className="py-4 text-center"><p className="text-xs text-gray-500">Open Invoices</p><p className="text-3xl font-bold text-yellow-600">{allInvoices.filter(i => i.status === 'open').length}</p></div></Card>
          </div>
          {/* Quick subscription table */}
          <Card padding={false}>
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="font-semibold">Recent Subscriptions</h3>
              <Button variant="ghost" size="sm" onClick={() => setTab('subscriptions')}>View All</Button>
            </div>
            {billingLoading ? <div className="p-4 text-gray-400 text-sm">Loading...</div> : (
              <Table columns={[
                { key: 'customer', header: 'Customer', render: (s: any) => <span className="font-medium">{s._customerName}</span> },
                { key: 'status', header: 'Status', render: (s: any) => <Badge color={statusColor(s.status)}>{s.status}</Badge> },
                { key: 'amount', header: 'Amount', render: (s: any) => fmtAmount((s.items?.data || []).reduce((sum: number, i: any) => sum + (i.price?.unit_amount || 0) * (i.quantity || 1), 0)) },
                { key: 'created', header: 'Created', render: (s: any) => fmtDate(s.created) },
              ]} data={allSubs.slice(0, 10)} emptyMessage="No subscriptions" />
            )}
          </Card>
        </div>
      )}

      {/* ═══ STANDARD RATES TAB ═══ */}
      {tab === 'standard' && (
        <div className="space-y-6">
          <Card padding={false}>
            <BillableItemsManager />
          </Card>
          <Card padding={false}>
            <div className="p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Legacy rate overrides</h3>
              <p className="text-sm text-gray-500 mt-0.5">Per-dealer / per-customer overrides applied on top of the items above. Will fold into BillableItemsManager in a future round.</p>
            </div>
            <RatesTable scopeType="standard" showStrategy />
          </Card>
        </div>
      )}

      {/* ═══ DEALERS TAB ═══ */}
      {tab === 'dealers' && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Dealer Rates</h3>
            <p className="text-sm text-gray-500 mt-0.5">Click a dealer to view/edit their custom rates</p>
          </div>
          <div className="divide-y divide-gray-100">
            {dealers.map((d: any) => (
              <div key={d.id}>
                <div className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedRateId(expandedRateId === d.id ? null : d.id)}>
                  <div>
                    <span className="font-medium text-gray-900">{d.company_name || d.name}</span>
                    <span className="text-xs text-gray-400 ml-2">{d.contact_email || d.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge color="green">{d.status || 'active'}</Badge>
                    <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedRateId === d.id ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                  </div>
                </div>
                {expandedRateId === d.id && (
                  <div className="bg-gray-50 border-t border-gray-100">
                    <RatesTable scopeType="dealer" scopeId={d.id} dealerId={d.id} showStrategy />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ═══ CUSTOMERS TAB ═══ */}
      {tab === 'customers' && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Customer Rates</h3>
            <p className="text-sm text-gray-500 mt-0.5">Click a customer to view/edit their custom rates</p>
          </div>
          <div className="divide-y divide-gray-100">
            {customers.map((c: any) => {
              const dealer = dealers.find((d: any) => d.id === c.integrator_id);
              return (
                <div key={c.id}>
                  <div className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedRateId(expandedRateId === c.id ? null : c.id)}>
                    <div>
                      <span className="font-medium text-gray-900">{c.company || c.company_name || c.name}</span>
                      {dealer && <span className="text-xs text-gray-400 ml-2">via {dealer.company_name || dealer.name}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge color="green">{c.status || 'active'}</Badge>
                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedRateId === c.id ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </div>
                  {expandedRateId === c.id && (
                    <div className="bg-gray-50 border-t border-gray-100">
                      <RatesTable scopeType="customer" scopeId={c.id} customerId={c.id} dealerId={c.integrator_id} showStrategy />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ═══ PROPERTIES TAB ═══ */}
      {tab === 'properties' && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Property Rates</h3>
            <p className="text-sm text-gray-500 mt-0.5">Click a property to view/edit custom rates</p>
          </div>
          <div className="divide-y divide-gray-100">
            {properties.map((p: any) => {
              const cust = customers.find((c: any) => c.id === p.customer_id);
              return (
                <div key={p.id}>
                  <div className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedRateId(expandedRateId === p.id ? null : p.id)}>
                    <div>
                      <span className="font-medium text-gray-900">{p.name}</span>
                      <span className="text-xs text-gray-400 ml-2">{p.address}, {p.city}</span>
                      {cust && <span className="text-xs text-gray-400 ml-2">({cust.company || cust.company_name || cust.name})</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge color="green">{p.status || 'active'}</Badge>
                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedRateId === p.id ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </div>
                  {expandedRateId === p.id && (
                    <div className="bg-gray-50 border-t border-gray-100">
                      <RatesTable scopeType="property" scopeId={p.id} propertyId={p.id} customerId={p.customer_id} dealerId={p.integrator_id} showStrategy />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ═══ SUBSCRIPTIONS TAB ═══ */}
      {tab === 'subscriptions' && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100 flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">All Subscriptions ({allSubs.length})</h3>
          </div>
          {billingLoading ? <div className="p-4 text-gray-400 text-sm">Loading subscriptions...</div> : (
            <div className="divide-y divide-gray-100">
              <div className="grid grid-cols-7 gap-2 px-4 py-2 text-xs font-medium text-gray-500 uppercase bg-gray-50">
                <span>Customer</span><span>Status</span><span>Amount</span><span>Start</span><span>Period</span><span>Payment</span><span>Actions</span>
              </div>
              {allSubs.map(s => {
                const items = s.items?.data || [];
                const total = items.reduce((sum: number, i: any) => sum + (i.price?.unit_amount || 0) * (i.quantity || 1), 0);
                return (
                  <div
                    key={s.id}
                    className="grid grid-cols-7 gap-2 px-4 py-3 text-sm items-center cursor-pointer hover:bg-gray-50"
                    onClick={() => setDetailSub(s)}
                  >
                    <span className="font-medium truncate">{s._customerName}</span>
                    <span><Badge color={statusColor(s.status)}>{s.status}</Badge></span>
                    <span className="font-medium">{fmtAmount(total)}</span>
                    <span className="text-gray-600 text-xs">{fmtDate(s.start_date || s.created)}</span>
                    <span className="text-gray-600 text-xs">{fmtDate(s.current_period_start)} – {fmtDate(s.current_period_end)}</span>
                    <span className="text-xs text-gray-500">{s.default_payment_method?.card ? `•••• ${s.default_payment_method.card.last4}` : '—'}</span>
                    <span onClick={e => e.stopPropagation()}><SubscriptionActions subscription={s} onUpdated={fetchStripeBilling} /></span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* ═══ INVOICES TAB ═══ */}
      {tab === 'invoices' && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100 flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">All Invoices ({allInvoices.length})</h3>
          </div>
          {billingLoading ? <div className="p-4 text-gray-400 text-sm">Loading invoices...</div> : (
            <div className="divide-y divide-gray-100">
              <div className="grid grid-cols-7 gap-2 px-4 py-2 text-xs font-medium text-gray-500 uppercase bg-gray-50">
                <span>Customer</span><span>Invoice</span><span>Date</span><span>Amount</span><span>Status</span><span>PDF</span><span>Actions</span>
              </div>
              {allInvoices.map(inv => {
                const lines = inv.lines?.data || [];
                const isExp = expandedInvId === inv.id;
                return (
                  <div key={inv.id}>
                    <div className="grid grid-cols-7 gap-2 px-4 py-3 text-sm items-center cursor-pointer hover:bg-gray-50" onClick={() => setExpandedInvId(isExp ? null : inv.id)}>
                      <span className="font-medium truncate">{inv._customerName}</span>
                      <span className="font-medium">{inv.number || '—'}</span>
                      <span className="text-gray-600">{fmtDate(inv.created)}</span>
                      <span className="font-medium">{fmtAmount(inv.amount_due)}</span>
                      <span><Badge color={statusColor(inv.status)}>{inv.status}</Badge></span>
                      <span onClick={e => e.stopPropagation()}>{inv.invoice_pdf ? <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline text-xs">Download</a> : '—'}</span>
                      <span className="flex gap-2" onClick={e => e.stopPropagation()}>
                        <button className="text-xs text-brand-600 hover:underline" onClick={() => { setSendEmailInvId(inv.id); setSendEmailAddr(inv.customer_email || ''); }}>Email</button>
                        {inv.status === 'open' && <button className="text-xs text-red-600 hover:underline" onClick={async () => { if (window.confirm('Void this invoice?')) { await fetch(`${STRIPE_PROXY_URL}/invoices/${inv.id}/void`, { method: 'POST', headers: { 'Content-Type': 'application/json' } }); fetchStripeBilling(); } }}>Void</button>}
                      </span>
                    </div>
                    {isExp && lines.length > 0 && (
                      <div className="bg-gray-50 px-8 py-3 border-t border-gray-100">
                        <p className="text-xs font-medium text-gray-500 mb-2">Line Items</p>
                        {lines.map((line: any, idx: number) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-gray-700">{line.description || line.price?.nickname || `Item ${idx + 1}`}</span>
                            <span className="font-medium">{fmtAmount(line.amount)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm font-semibold border-t border-gray-200 pt-1 mt-1">
                          <span>Total</span><span>{fmtAmount(inv.amount_due)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* ═══ PRODUCTS & PRICES TAB ═══ */}
      {tab === 'products' && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Stripe Products & Prices</h3>
            <p className="text-sm text-gray-500 mt-0.5">View and edit all Stripe products, prices, and billing configuration</p>
          </div>
          <ProductsManager />
        </Card>
      )}

      {/* ═══ SERVICES & COSTS TAB ═══ */}
      {tab === 'services' && (
        <Card padding={false}>
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Connected Services & Costs</h3>
            <p className="text-sm text-gray-500 mt-0.5">Platform service costs, margins, and revenue tracking</p>
          </div>
          <ServiceCostsTab totalRevenueCents={Math.round(totalMonthly * 100)} />
        </Card>
      )}

      {/* ═══ MODALS ═══ */}
      <SubscriptionDetailModal
        open={!!detailSub}
        onClose={() => setDetailSub(null)}
        subscriptionId={detailSub?.id || null}
        customerName={detailSub?._customerName}
      />

      <CreateSubscriptionModal open={createSubOpen} onClose={() => setCreateSubOpen(false)}
        defaultCustomerId="" customers={customers.map(c => ({ id: c.id, company_name: c.company || c.company_name || c.name, stripe_customer_id: c.stripe_customer_id }))}
        onCreated={fetchStripeBilling} />

      <CreateInvoiceModal open={createInvOpen} onClose={() => setCreateInvOpen(false)}
        defaultCustomerId="" customers={customers.map(c => ({ id: c.id, company_name: c.company || c.company_name || c.name, stripe_customer_id: c.stripe_customer_id }))}
        onCreated={fetchStripeBilling} />

      <Modal open={!!sendEmailInvId} onClose={() => setSendEmailInvId(null)} title="Send Invoice">
        <Input label="Email Address" type="email" value={sendEmailAddr} onChange={(e: any) => setSendEmailAddr(e.target.value)} />
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="secondary" onClick={() => setSendEmailInvId(null)}>Cancel</Button>
          <Button onClick={async () => { if (sendEmailInvId && sendEmailAddr) { await fetch(`${STRIPE_PROXY_URL}/invoices/${sendEmailInvId}/send`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: sendEmailAddr }) }).catch(() => {}); setSendEmailInvId(null); } }}>Send</Button>
        </div>
      </Modal>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   CUSTOMER BILLING VIEW (non-admin)
   ═══════════════════════════════════════════════════════════════ */

const CustomerBilling: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const handlePortal = async () => {
    setLoading(true);
    try {
      const { url } = await stripeApi.createBillingPortal('', window.location.href);
      window.location.href = url;
    } catch (err: any) {
      alert(err.message || 'Failed to open billing portal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
        <p className="text-gray-500 mt-1">Manage your payment methods and view invoices</p>
      </div>
      <Card>
        <div className="text-center py-12">
          <div className="text-5xl mb-4">💳</div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Manage Your Billing</h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            View your invoices, update payment methods, and manage your subscription through our secure billing portal.
          </p>
          <Button onClick={handlePortal} disabled={loading}>
            {loading ? 'Opening...' : 'Open Billing Portal'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

/* ═══ MAIN EXPORT ═══ */

export const Billing: React.FC = () => {
  const { user } = useAuth();
  if (!user) return null;
  return isAdmin(user.role) ? <AdminBilling /> : <CustomerBilling />;
};
